/**
 * Veylen buy box behaviour.
 *
 * <veylen-purchase>  toggles one-time vs subscription and keeps the product
 *                    form's hidden selling_plan input in sync with the tier.
 * <veylen-ship-by>   resolves the next real dispatch cut-off and counts down.
 *
 * Both are plain custom elements with no theme imports, so they survive the
 * variant-picker morph without re-registration.
 */

/**
 * The tier radios are the variant picker, so Shopify preselects whichever
 * variant is first available -- tier 1. The merchandising brief wants the
 * 3-month bundle preselected instead. Selecting it through a real change event
 * drives the normal variant pipeline (price, media, hidden variant input), so
 * nothing falls out of sync. Runs once per page load, and never when the URL
 * already names a variant, so a shared link still wins.
 */
let defaultTierApplied = false;

class VeylenPurchase extends HTMLElement {
  connectedCallback() {
    this.addEventListener('change', this.#onChange);
    // Capture phase so this pre-empts the theme's own submit handling.
    document.addEventListener('click', this.#onBuyClick, true);
    this.#applyDefaultTier();
    this.sync();
  }

  disconnectedCallback() {
    this.removeEventListener('change', this.#onChange);
    document.removeEventListener('click', this.#onBuyClick, true);
  }

  /** Preselect the merchandised tier on first load. */
  #applyDefaultTier() {
    if (defaultTierApplied) return;

    const wanted = parseInt(this.dataset.defaultTier || '0', 10);
    if (!wanted) return;

    // A variant in the URL is an explicit choice and must not be overridden.
    if (new URLSearchParams(window.location.search).has('variant')) {
      defaultTierApplied = true;
      return;
    }

    const radios = [...this.querySelectorAll('.vy-bundle input[type="radio"]')];
    const target = radios[wanted - 1];
    if (!target || target.checked || target.dataset.optionAvailable === 'false') {
      defaultTierApplied = true;
      return;
    }

    defaultTierApplied = true;
    target.checked = true;
    target.dispatchEvent(new Event('change', { bubbles: true }));
  }

  get #form() {
    return this.dataset.formId ? document.getElementById(this.dataset.formId) : null;
  }

  /** Send the shopper straight to checkout instead of opening the cart drawer. */
  #onBuyClick = (event) => {
    if (this.dataset.directCheckout !== 'true') return;

    const target = event.target instanceof Element ? event.target : null;
    const button = target?.closest('.add-to-cart-button');
    if (!button || button.disabled) return;

    const form = this.#form;
    // The sticky bar proxies a click onto the same button, so both paths land here.
    if (!form || !(form.contains(button) || button.closest('sticky-add-to-cart'))) return;

    event.preventDefault();
    event.stopImmediatePropagation();
    this.#checkout(button, form);
  };

  async #checkout(button, form) {
    if (this.#busy) return;
    this.#busy = true;
    button.setAttribute('aria-busy', 'true');

    try {
      const addUrl = window.Theme?.routes?.cart_add_url || '/cart/add.js';
      const body = new FormData(form);

      // The variant id in the form only updates after the picker's async
      // re-render. Re-derive both values from the checked tier so a fast click
      // can never post one tier's variant with another tier's plan.
      const checkedTier = this.querySelector('.vy-bundle input:checked');
      const card = checkedTier?.closest('.vy-bundle');
      const variantId = checkedTier?.dataset.variantId;
      const planId = card?.dataset.sellingPlan;

      if (variantId) body.set('id', variantId);
      if (this.dataset.purchaseType === 'subscribe' && planId) {
        body.set('selling_plan', planId);
      } else {
        body.delete('selling_plan');
      }

      const response = await fetch(addUrl, {
        method: 'POST',
        headers: { Accept: 'application/json' },
        body,
      });

      if (!response.ok) throw new Error(`Add to cart failed: ${response.status}`);
      window.location.href = '/checkout';
    } catch (error) {
      console.error('[veylen] direct checkout failed, falling back to the cart', error);
      window.location.href = '/cart';
    } finally {
      this.#busy = false;
      button.removeAttribute('aria-busy');
    }
  }

  #busy = false;

  #onChange = (event) => {
    const target = event.target;
    if (!(target instanceof HTMLInputElement)) return;

    if (target.name === this.dataset.toggleName) {
      this.dataset.purchaseType = target.value;
    }

    // Tier changes re-render asynchronously; settle after the morph.
    requestAnimationFrame(() => this.sync());
  };

  /** Point the hidden selling_plan input at the selected tier's plan. */
  sync() {
    const input = this.querySelector('[data-veylen-selling-plan]');
    if (!(input instanceof HTMLInputElement)) return;

    const subscribing = this.dataset.purchaseType === 'subscribe';
    const checkedTier = this.querySelector('.vy-bundle input:checked');
    const planId = checkedTier?.closest('.vy-bundle')?.dataset.sellingPlan || '';

    if (subscribing && planId) {
      input.value = planId;
      input.disabled = false;
    } else {
      // A disabled input is left out of FormData, so one-time orders post no plan.
      input.value = '';
      input.disabled = true;
    }

    this.#syncStickyPrice(checkedTier);
  }

  /**
   * The sticky bar renders the plain variant price server-side, which is wrong
   * whenever a subscription is selected. Mirror the tier's visible price instead.
   */
  #syncStickyPrice(checkedTier) {
    const stickyPrice = document.querySelector('.sticky-add-to-cart__price');
    if (!stickyPrice) return;

    const card = checkedTier?.closest('.vy-bundle');
    const marker = this.dataset.purchaseType === 'subscribe' ? 'data-price-subscribe' : 'data-price-onetime';
    const source = card?.querySelector(`.vy-bundle__prices [${marker}] .vy-bundle__price`);
    if (source) stickyPrice.textContent = source.textContent.trim();
  }
}

class VeylenShipBy extends HTMLElement {
  #timer;

  connectedCallback() {
    this.render();
    this.#timer = setInterval(() => this.render(), 1000);
  }

  disconnectedCallback() {
    clearInterval(this.#timer);
  }

  get #timezone() {
    return this.dataset.timezone || Intl.DateTimeFormat().resolvedOptions().timeZone || 'UTC';
  }

  get #shipDays() {
    const raw = (this.dataset.shipDays || '1,2,3,4,5').split(',').map((d) => Number.parseInt(d.trim(), 10));
    const days = raw.filter((d) => Number.isInteger(d) && d >= 0 && d <= 6);
    return days.length ? days : [1, 2, 3, 4, 5];
  }

  /** Current wall-clock time in the shop's timezone, expressed as a UTC-keyed Date. */
  #now() {
    const parts = new Intl.DateTimeFormat('en-US', {
      timeZone: this.#timezone,
      hour12: false,
      year: 'numeric',
      month: '2-digit',
      day: '2-digit',
      hour: '2-digit',
      minute: '2-digit',
      second: '2-digit',
    })
      .formatToParts(new Date())
      .filter((p) => p.type !== 'literal');

    const p = Object.fromEntries(parts.map((x) => [x.type, x.value]));
    const hour = p.hour === '24' ? 0 : Number(p.hour);

    return new Date(Date.UTC(Number(p.year), Number(p.month) - 1, Number(p.day), hour, Number(p.minute), Number(p.second)));
  }

  /** The next cut-off that falls on a dispatch day. */
  #nextCutoff(now) {
    const hour = Number.parseInt(this.dataset.cutoffHour || '14', 10);
    const minute = Number.parseInt(this.dataset.cutoffMinute || '0', 10);
    const shipDays = this.#shipDays;

    for (let offset = 0; offset < 14; offset++) {
      const candidate = new Date(now);
      candidate.setUTCDate(candidate.getUTCDate() + offset);
      candidate.setUTCHours(hour, minute, 0, 0);

      if (candidate > now && shipDays.includes(candidate.getUTCDay())) return candidate;
    }

    return null;
  }

  render() {
    const now = this.#now();
    const cutoff = this.#nextCutoff(now);
    if (!cutoff) return;

    const dateEl = this.querySelector('[data-ship-date]');
    const countdownEl = this.querySelector('[data-ship-countdown]');
    const labelEl = this.querySelector('[data-ship-when]');

    if (dateEl) {
      dateEl.textContent = new Intl.DateTimeFormat(this.dataset.locale || undefined, {
        timeZone: 'UTC',
        weekday: 'long',
        month: 'long',
        day: 'numeric',
      }).format(cutoff);
    }

    if (labelEl) {
      const dayDelta = Math.round((Date.UTC(cutoff.getUTCFullYear(), cutoff.getUTCMonth(), cutoff.getUTCDate()) -
        Date.UTC(now.getUTCFullYear(), now.getUTCMonth(), now.getUTCDate())) / 86400000);

      labelEl.textContent =
        dayDelta === 0
          ? this.dataset.labelToday || 'today'
          : dayDelta === 1
          ? this.dataset.labelTomorrow || 'tomorrow'
          : new Intl.DateTimeFormat(this.dataset.locale || undefined, { timeZone: 'UTC', weekday: 'long' }).format(cutoff);
    }

    if (countdownEl) {
      const remaining = Math.max(0, cutoff.getTime() - now.getTime());
      const totalSeconds = Math.floor(remaining / 1000);
      const hours = Math.floor(totalSeconds / 3600);
      const minutes = Math.floor((totalSeconds % 3600) / 60);
      const seconds = totalSeconds % 60;
      countdownEl.textContent = `${hours}h ${String(minutes).padStart(2, '0')}m ${String(seconds).padStart(2, '0')}s`;
    }

    this.dataset.ready = 'true';
  }
}

/**
 * In-page CTAs back to the buy box.
 *
 * Horizon opts into cross-document view transitions, which swallows the
 * browser's native scroll-to-fragment: the hash updates but the page never
 * moves. Scoped to our own anchor so no other in-page link is affected.
 */
document.addEventListener('click', (event) => {
  const link = event.target instanceof Element ? event.target.closest('a[href="#veylen-buy"]') : null;
  if (!link) return;

  const target = document.getElementById('veylen-buy');
  if (!target) return;

  event.preventDefault();
  const reduced = window.matchMedia('(prefers-reduced-motion: reduce)').matches;
  target.scrollIntoView({ behavior: reduced ? 'auto' : 'smooth', block: 'start' });
  history.replaceState(null, '', '#veylen-buy');
});

/**
 * TEMPORARY on-device diagnostic — add ?vydebug=1 to any URL.
 * Inert without the parameter. Remove once the culprit is identified.
 *
 * Reports what desktop tooling cannot see from here: the real zoom state,
 * whether the document is genuinely pannable, and which CSS build the device
 * actually loaded (Shopify's CDN has served stale assets for many minutes).
 */
if (location.search.includes('vydebug')) {
  window.addEventListener('load', () => {
    setTimeout(() => {
      const de = document.documentElement;
      const limit = de.clientWidth;

      // Which veylen stylesheet did this device really load?
      // Match on the FILENAME, not the href -- the domain itself contains
      // "veylen", so an href test would happily report base.css.
      const sheet = [...document.querySelectorAll('link[rel=stylesheet]')]
        .map((l) => (l.href || '').split('/').pop().split('?')[0])
        .filter((f) => f.startsWith('veylen'))[0] || 'none';

      // Is the document actually pannable? Probe it rather than infer.
      const probe = (el) => {
        if (!el) return 0;
        const was = el.scrollLeft;
        el.scrollLeft = 99999;
        const max = Math.round(el.scrollLeft);
        el.scrollLeft = was;
        return max;
      };
      const panDoc = probe(document.scrollingElement);
      const panBody = probe(document.body);

      const vv = window.visualViewport;
      const zoom = vv ? Math.round(vv.scale * 100) / 100 : 1;

      // Unclipped elements reaching past the edge (layout-level overflow only)
      const clipped = (el) => {
        let p = el.parentElement;
        while (p && p !== de) {
          const cs = getComputedStyle(p);
          if (cs.overflowX !== 'visible' || cs.overflowY !== 'visible') return true;
          p = p.parentElement;
        }
        return false;
      };
      const hits = [];
      document.querySelectorAll('body *').forEach((el) => {
        const r = el.getBoundingClientRect();
        if (r.width < 4 || r.right <= limit + 0.5 || clipped(el)) return;
        hits.push({ el, right: Math.round(r.right), w: Math.round(r.width) });
      });
      hits.sort((a, b) => b.right - a.right);
      hits.slice(0, 5).forEach((h) => (h.el.style.outline = '3px solid red'));

      const name = (el) =>
        (el.tagName.toLowerCase() +
          (el.className ? '.' + el.className.toString().trim().split(/\s+/).slice(0, 2).join('.') : '')
        ).slice(0, 40);

      const row = (k, v) => '<tr><td style="opacity:.7;padding-right:10px">' + k + '</td><td><b>' + v + '</b></td></tr>';

      const panel = document.createElement('div');
      panel.style.cssText =
        'position:fixed;inset:auto 6px 6px 6px;z-index:2147483647;background:#0b0b0b;color:#4ade80;' +
        'font:13px/1.5 ui-monospace,monospace;padding:12px 14px;border-radius:10px;' +
        'max-height:60vh;overflow:auto;box-shadow:0 0 0 2px #4ade80';
      panel.innerHTML =
        '<div style="color:#fff;font-weight:700;margin-bottom:6px">VY DIAGNOSTIC — tap to dismiss</div>' +
        '<table>' +
        row('css build', sheet) +
        row('viewport', limit + 'px') +
        row('scrollWidth', de.scrollWidth + 'px') +
        row('overflow', (de.scrollWidth - limit) + 'px') +
        row('pannable (doc)', panDoc + 'px') +
        row('pannable (body)', panBody + 'px') +
        row('zoom', zoom + (zoom !== 1 ? '  <-- ZOOMED' : '')) +
        row('screen', (vv ? Math.round(vv.width) : '?') + ' / ' + window.innerWidth) +
        '</table>' +
        '<div style="margin-top:6px">' +
        (hits.length
          ? hits.slice(0, 5).map((h) => name(h.el) + ' w:' + h.w + ' r:' + h.right).join('<br>')
          : 'no unclipped overflowing element') +
        '</div>';
      panel.addEventListener('click', () => panel.remove());
      document.body.appendChild(panel);
    }, 900);
  });
}

if (!customElements.get('veylen-purchase')) customElements.define('veylen-purchase', VeylenPurchase);
if (!customElements.get('veylen-ship-by')) customElements.define('veylen-ship-by', VeylenShipBy);
